// Client for the local kiosk bridge. The browser SPA never touches the
// serial port directly -- every bridge interaction goes through here.
//
// Two transports, picked automatically at construction:
//   - HttpBridgeTransport: the legacy Windows-prototype path, talking to
//     the Node bridge over loopback HTTP/WS at 127.0.0.1:8787. Used by
//     the desktop dev workflow (Vite + Node bridge + Chrome).
//   - AndroidBridgeTransport: the production path on the iMin Crane 1
//     when the SPA is hosted in the kiosk-android WebView. Talks to
//     window.AndroidBridge (a Kotlin @JavascriptInterface) and receives
//     push events through window.__bridgeEvents.
//
// The picker key is the presence of window.AndroidBridge. The public
// BridgeClient API is identical regardless.
//
// See ../../../docs/kiosk/android-port.md for the full bridge contract.
import {
  BRIDGE_DEFAULT_HOST,
  BRIDGE_DEFAULT_PORT,
  type CloseResponse,
  closeResponseSchema,
  type HealthResponse,
  healthResponseSchema,
  type LastSaleResponse,
  lastSaleResponseSchema,
  type PrintJob,
  type PrintResponse,
  printResponseSchema,
  type SaleOutcome,
  type SaleRequest,
  saleOutcomeSchema,
  type WsEvent,
  wsEventSchema,
} from "@entradart/kiosk-shared/bridge";

export class BridgeError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "BridgeError";
    this.status = status;
  }
}

// Thrown when the terminal DID answer (the native push carries an approved or
// declined verdict) but the payload failed the shared schema. The verdict is
// unknown to us, not absent -- App maps this to "verify your charge", never
// to the "no communication" gloss that implies no charge happened.
export class BridgeOutcomeError extends BridgeError {
  rawEvent: unknown;
  constructor(message: string, rawEvent: unknown) {
    super(message, 0);
    this.name = "BridgeOutcomeError";
    this.rawEvent = rawEvent;
  }
}

export type BridgeDiagnostic =
  | { kind: "sale.parse_error"; rawEvent: unknown; issues: string }
  | { kind: "sale.frames"; requestId: string; frames: unknown };

export type BridgeSubscribeHandlers = {
  onEvent: (event: WsEvent) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (error: Error) => void;
};

interface BridgeTransport {
  health(): Promise<HealthResponse>;
  sale(req: SaleRequest): Promise<SaleOutcome>;
  print(job: PrintJob): Promise<PrintResponse>;
  lastSale(): Promise<LastSaleResponse>;
  close(): Promise<CloseResponse>;
  subscribe(handlers: BridgeSubscribeHandlers): () => void;
}

export type BridgeClientOptions = {
  host?: string;
  port?: number;
  fetchImpl?: typeof fetch;
  // Test seam: inject a transport directly to bypass the
  // window.AndroidBridge / HTTP picker.
  transport?: BridgeTransport;
  // Telemetry hook for transport-level anomalies (unparseable sale results,
  // raw IM30 frames forwarded by newer APKs). Android transport only.
  onDiagnostic?: (d: BridgeDiagnostic) => void;
};

export class BridgeClient {
  private readonly transport: BridgeTransport;

  constructor(opts: BridgeClientOptions = {}) {
    this.transport = opts.transport ?? pickDefaultTransport(opts);
  }

  health(): Promise<HealthResponse> {
    return this.transport.health();
  }

  sale(body: SaleRequest): Promise<SaleOutcome> {
    return this.transport.sale(body);
  }

  print(body: PrintJob): Promise<PrintResponse> {
    return this.transport.print(body);
  }

  lastSale(): Promise<LastSaleResponse> {
    return this.transport.lastSale();
  }

  close(): Promise<CloseResponse> {
    return this.transport.close();
  }

  subscribe(handlers: BridgeSubscribeHandlers): () => void {
    return this.transport.subscribe(handlers);
  }
}

function pickDefaultTransport(opts: BridgeClientOptions): BridgeTransport {
  const w = typeof window !== "undefined" ? window : undefined;
  if (w?.AndroidBridge) {
    return new AndroidBridgeTransport(w.AndroidBridge, opts.onDiagnostic);
  }
  return new HttpBridgeTransport(opts);
}

// ---------------------------------------------------------------------------
// HTTP transport (legacy Windows-prototype path).

class HttpBridgeTransport implements BridgeTransport {
  private readonly base: string;
  private readonly wsBase: string;
  private readonly fetchImpl: typeof fetch;

  constructor(opts: BridgeClientOptions) {
    const host = opts.host ?? BRIDGE_DEFAULT_HOST;
    const port = opts.port ?? BRIDGE_DEFAULT_PORT;
    this.base = `http://${host}:${port}`;
    this.wsBase = `ws://${host}:${port}`;
    // Bind to the global so `this.fetchImpl(...)` doesn't invoke native
    // fetch with this transport as receiver -- Chromium throws "Illegal
    // invocation" for that. Injected test mocks don't care about `this`.
    this.fetchImpl = opts.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  health(): Promise<HealthResponse> {
    return this.get("/health", healthResponseSchema);
  }

  sale(body: SaleRequest): Promise<SaleOutcome> {
    return this.post("/sale", body, saleOutcomeSchema);
  }

  print(body: PrintJob): Promise<PrintResponse> {
    return this.post("/print", body, printResponseSchema);
  }

  lastSale(): Promise<LastSaleResponse> {
    return this.post("/lastSale", {}, lastSaleResponseSchema);
  }

  close(): Promise<CloseResponse> {
    return this.post("/close", {}, closeResponseSchema);
  }

  subscribe(handlers: BridgeSubscribeHandlers): () => void {
    const ws = new WebSocket(`${this.wsBase}/events`);
    // A close we triggered ourselves (via the returned unsubscribe) isn't a
    // bridge failure — without this guard a React StrictMode remount, or any
    // intentional teardown, would surface as `onClose` and bounce the kiosk
    // to "out of service".
    let unsubscribed = false;
    ws.addEventListener("message", (e) => {
      try {
        const parsed = wsEventSchema.parse(JSON.parse(String(e.data)));
        handlers.onEvent(parsed);
      } catch (err) {
        handlers.onError?.(err instanceof Error ? err : new Error(String(err)));
      }
    });
    ws.addEventListener("open", () => handlers.onOpen?.());
    ws.addEventListener("close", () => {
      if (!unsubscribed) {
        handlers.onClose?.();
      }
    });
    ws.addEventListener("error", () => {
      if (!unsubscribed) {
        handlers.onError?.(new Error("websocket error"));
      }
    });
    return () => {
      unsubscribed = true;
      try {
        ws.close();
      } catch {
        // Closing an already-closed socket is fine.
      }
    };
  }

  private async get<T>(
    path: string,
    schema: { parse: (input: unknown) => T }
  ): Promise<T> {
    const response = await this.fetchImpl(`${this.base}${path}`, {
      method: "GET",
    });
    return await this.handleResponse(response, schema);
  }

  private async post<T>(
    path: string,
    body: unknown,
    schema: { parse: (input: unknown) => T }
  ): Promise<T> {
    const response = await this.fetchImpl(`${this.base}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return await this.handleResponse(response, schema);
  }

  private async handleResponse<T>(
    response: Response,
    schema: { parse: (input: unknown) => T }
  ): Promise<T> {
    if (!response.ok) {
      const text = await response.text();
      throw new BridgeError(
        `Bridge ${response.status}: ${text || response.statusText}`,
        response.status
      );
    }
    const json = (await response.json()) as unknown;
    return schema.parse(json);
  }
}

// ---------------------------------------------------------------------------
// Android transport (production path inside the kiosk-android WebView).

// Synchronous methods return JSON strings. Async methods return immediately;
// the Kotlin side delivers the eventual outcome by pushing a *.result event
// onto window.__bridgeEvents, keyed by a SPA-issued requestId.
export type AndroidBridgeApi = {
  health(): string;
  // Per-device runtime config as a JSON string matching
  // kioskRuntimeConfigSchema. Synchronous and cheap, like health().
  config(): string;
  startSale(jsonRequest: string, requestId: string): void;
  print(jsonRequest: string, requestId: string): void;
  lastSale(requestId: string): void;
  close(requestId: string): void;
};

// The Kotlin side calls window.__bridgeEvents.push(event) on the UI thread
// via WebView.evaluateJavascript. The transport installs this hook at
// construction so events arriving before subscribe() aren't dropped on the
// floor (they queue if no subscribers are attached yet — see below).
export type BridgeEventBus = {
  push(event: NativePushEvent): void;
};

declare global {
  interface Window {
    AndroidBridge?: AndroidBridgeApi;
    __bridgeEvents?: BridgeEventBus;
  }
}

// Result events are private to the Android transport — the SPA never sees
// them. Subscriber-visible events use the public WsEvent schema verbatim.
type SaleResultEvent = {
  type: "sale.result";
  requestId: string;
  outcome: SaleOutcome;
  // Raw IM30 frames of the sale, sent by newer APKs only. Opaque here; it is
  // forwarded to onDiagnostic untouched.
  frames?: unknown;
};
type PrintResultEvent = {
  type: "print.result";
  requestId: string;
  response: PrintResponse;
};
type LastSaleResultEvent = {
  type: "lastSale.result";
  requestId: string;
  response: LastSaleResponse;
};
type CloseResultEvent = {
  type: "close.result";
  requestId: string;
  response: CloseResponse;
};

type NativePushEvent =
  | WsEvent
  | SaleResultEvent
  | PrintResultEvent
  | LastSaleResultEvent
  | CloseResultEvent;

type PendingResolver = (event: NativePushEvent) => void;

class AndroidBridgeTransport implements BridgeTransport {
  // Every live transport of this module instance. window.__bridgeEvents fans
  // out to all of them because React StrictMode (dev builds) double-invokes
  // the useMemo that constructs the BridgeClient: two transports get built,
  // and binding the bus to only the latest one silently starves whichever
  // instance React actually kept — its pending sale.result resolver never
  // fires and the kiosk hangs on "awaiting card". Fan-out is safe: requestIds
  // are resolved from each transport's own pending map, and only the kept
  // client's transport ever has subscribers or pending calls.
  private static readonly instances: Set<AndroidBridgeTransport> = new Set();

  private readonly api: AndroidBridgeApi;
  private readonly onDiagnostic?: (d: BridgeDiagnostic) => void;
  private readonly subscribers: Set<BridgeSubscribeHandlers> = new Set();
  private readonly pending: Map<string, PendingResolver> = new Map();
  private nextRequestId = 1;

  constructor(
    api: AndroidBridgeApi,
    onDiagnostic?: (d: BridgeDiagnostic) => void
  ) {
    this.api = api;
    this.onDiagnostic = onDiagnostic;
    AndroidBridgeTransport.instances.add(this);
    // Install the global event bus the Kotlin side pushes to. We rebind on
    // every construction so a hot-reloaded SPA gets a fresh dispatcher (the
    // reloaded module brings its own empty `instances` set, dropping the
    // stale pre-reload transports).
    if (typeof window !== "undefined") {
      window.__bridgeEvents = {
        push: (event) => {
          for (const transport of AndroidBridgeTransport.instances) {
            transport.dispatch(event);
          }
        },
      };
    }
  }

  health(): Promise<HealthResponse> {
    const json = this.api.health();
    return Promise.resolve(healthResponseSchema.parse(JSON.parse(json)));
  }

  sale(body: SaleRequest): Promise<SaleOutcome> {
    return this.callAsync("sale.result", (requestId) => {
      this.api.startSale(JSON.stringify(body), requestId);
    }).then((event) => {
      if (event.type !== "sale.result") {
        throw new Error(`expected sale.result, got ${event.type}`);
      }
      if (event.frames !== undefined) {
        this.emit({
          kind: "sale.frames",
          requestId: event.requestId,
          frames: event.frames,
        });
      }
      const parsed = saleOutcomeSchema.safeParse(event.outcome);
      if (parsed.success) return parsed.data;
      const issues = parsed.error.message;
      this.emit({
        kind: "sale.parse_error",
        rawEvent: { outcome: event.outcome },
        issues,
      });
      const verdict = (event.outcome as { outcome?: unknown } | null)?.outcome;
      if (verdict === "approved" || verdict === "declined") {
        throw new BridgeOutcomeError(
          `sale.result failed schema: ${issues}`,
          event.outcome
        );
      }
      throw new BridgeError(`sale.result failed schema: ${issues}`, 0);
    });
  }

  print(body: PrintJob): Promise<PrintResponse> {
    return this.callAsync("print.result", (requestId) => {
      this.api.print(JSON.stringify(body), requestId);
    }).then((event) => {
      if (event.type !== "print.result") {
        throw new Error(`expected print.result, got ${event.type}`);
      }
      return printResponseSchema.parse(event.response);
    });
  }

  lastSale(): Promise<LastSaleResponse> {
    return this.callAsync("lastSale.result", (requestId) => {
      this.api.lastSale(requestId);
    }).then((event) => {
      if (event.type !== "lastSale.result") {
        throw new Error(`expected lastSale.result, got ${event.type}`);
      }
      return lastSaleResponseSchema.parse(event.response);
    });
  }

  close(): Promise<CloseResponse> {
    return this.callAsync("close.result", (requestId) => {
      this.api.close(requestId);
    }).then((event) => {
      if (event.type !== "close.result") {
        throw new Error(`expected close.result, got ${event.type}`);
      }
      return closeResponseSchema.parse(event.response);
    });
  }

  subscribe(handlers: BridgeSubscribeHandlers): () => void {
    this.subscribers.add(handlers);
    // The Kotlin side is always "open" once the WebView is loaded. We never
    // emit onClose -- if the native process dies, the WebView dies with it.
    handlers.onOpen?.();
    return () => {
      this.subscribers.delete(handlers);
    };
  }

  // Telemetry is best-effort: a throwing onDiagnostic must never turn an
  // approved sale into a rejection or mask a BridgeOutcomeError.
  private emit(d: BridgeDiagnostic): void {
    try {
      this.onDiagnostic?.(d);
    } catch {
      // telemetry must not affect the sale
    }
  }

  private callAsync(
    expectedType: NativePushEvent["type"],
    invoke: (requestId: string) => void
  ): Promise<NativePushEvent> {
    const requestId = `req-${this.nextRequestId++}`;
    return new Promise((resolve, reject) => {
      this.pending.set(requestId, resolve);
      try {
        invoke(requestId);
      } catch (err) {
        this.pending.delete(requestId);
        const message = err instanceof Error ? err.message : String(err);
        reject(new BridgeError(`AndroidBridge.${expectedType}: ${message}`, 0));
      }
    });
  }

  private dispatch(event: NativePushEvent): void {
    if (
      event.type === "sale.result" ||
      event.type === "print.result" ||
      event.type === "lastSale.result" ||
      event.type === "close.result"
    ) {
      const resolver = this.pending.get(event.requestId);
      if (resolver) {
        this.pending.delete(event.requestId);
        resolver(event);
      }
      // No matching pending request -- silently drop. This shouldn't happen
      // in practice but it's safer than throwing inside a JS-thread push.
      return;
    }
    // Subscriber-visible event. Validate against the public schema so a
    // malformed native push doesn't silently corrupt the reducer.
    const result = wsEventSchema.safeParse(event);
    if (!result.success) {
      for (const sub of this.subscribers) {
        sub.onError?.(
          new Error(`Invalid bridge event from native: ${result.error.message}`)
        );
      }
      return;
    }
    for (const sub of this.subscribers) {
      sub.onEvent(result.data);
    }
  }
}
